/**
 * CustomAbasPermissoes.tsx — O catálogo de ações em ABAS, uma por módulo.
 *
 * POR QUE ABAS, e não uma lista só: são 64 ações em 9 módulos. Empilhadas, viram
 * uma parede de switches em que ninguém acha nada — e o desenho do tema
 * (`adminux-company-user-roles`) mostra poucas linhas por vez justamente porque
 * é assim que se lê.
 *
 * CADA AÇÃO DIZ O QUE FAZ, em uma linha, embaixo do nome. O nome sozinho
 * ("Reabrir encerrada") não basta para decidir se alguém deve poder — e o
 * código técnico (`viagens.viagem.reabrir`), que estava no tooltip, respondia à
 * pergunta errada: ele diz onde a regra mora, não o que ela permite.
 *
 * SERVE AOS DOIS LADOS da frente: o pacote do PERFIL e a exceção da PESSOA
 * mostram o mesmo catálogo com regras de leitura diferentes. Quem chama diz,
 * por ação, se está ligada, se pode ser mexida, que etiqueta carrega (do perfil
 * / extra / retirado) e se está **alterada e ainda não salva** — o resto é
 * igual nos dois e mora aqui.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';

export interface Acao {
  codigo: string;
  nome: string;
  descricao?: string;
}

export interface Recurso {
  codigo: string;
  nome: string;
  acoes: Acao[];
}

export interface Modulo {
  codigo: string;
  nome: string;
  recursos: Recurso[];
}

/** Como uma ação aparece para quem está olhando. */
export interface EstadoAcao {
  ligada: boolean;
  editavel: boolean;
  /**
   * De onde vem o acesso, como sinal — nunca como badge de texto, que repetido
   * 42 vezes vira parede de etiqueta.
   *
   * DUAS COLUNAS FIXAS, e a ordem tem um porquê. À direita, `perfil`: a marca
   * do que o perfil dá, que fica lá **sempre**, inclusive quando a pessoa está
   * sem aquilo. Assim a coluna responde, sozinha, "o que ela deveria ter" — a
   * pergunta que o perfil existe para responder. À esquerda, `excecao`: o
   * desvio daquela pessoa em relação a isso.
   *
   * Ler as duas juntas dá a frase inteira: gente sozinha = tem porque o perfil
   * dá; menos + gente = o perfil dá e tiraram dela; mais sozinho = ninguém do
   * grupo tem, ela tem.
   */
  perfil?: { icone: string; classe: string; titulo: string };
  excecao?: { icone: string; classe: string; titulo: string };
  /** Difere do que está salvo — ganha destaque até salvar ou descartar. */
  alterada?: boolean;
  /** Sobrescreve a descrição do catálogo (o caso do que é só de admin). */
  aviso?: ReactNode;
}

export interface AbaExtra {
  codigo: string;
  nome: string;
  icone: string;
  conteudo: ReactNode;
}

// O mesmo ícone que cada módulo tem no menu lateral — a aba e o item de menu
// falam da mesma coisa, e reconhecer pelo desenho poupa ler.
const ICONE: Record<string, string> = {
  'ordens-servico': 'bi-clipboard-check',
  agenda: 'bi-calendar-week',
  viagens: 'bi-suitcase-lg',
  'custos-pedido': 'bi-bar-chart-line',
  autorizacoes: 'bi-patch-check',
  despesas: 'bi-cash-coin',
  funcionarios: 'bi-people',
  usuarios: 'bi-person-badge',
  configuracoes: 'bi-gear',
};

/**
 * Uma coluna de switches — a LOJA, na tela da pessoa (migration 126: o acesso
 * tem âmbito). A caixa no topo é "a pessoa está nesta loja": desmarcada, a
 * coluna inteira apaga, porque sem estar na loja não há pacote a valer lá.
 */
export interface Coluna {
  id: number;
  rotulo: string;
  ativa: boolean;
  /** A caixa do topo mudou e ainda não foi salva. */
  alterada?: boolean;
  aoAlternar: () => void;
}

interface Props {
  catalogo: Modulo[];
  /** O estado da linha — com colunas, é a UNIÃO delas (conta as abas). */
  estadoDe: (permissao: string) => EstadoAcao;
  aoAlternar: (permissao: string) => void;
  /** Ausente = sem switch de módulo inteiro (é o caso do perfil admin). */
  aoAlternarModulo?: (modulo: Modulo, ligar: boolean) => void;
  abasExtras?: AbaExtra[];
  /** Presentes = um switch por coluna em vez de um por linha. */
  colunas?: Coluna[];
  estadoNaColuna?: (permissao: string, coluna: number) => EstadoAcao;
  aoAlternarNaColuna?: (permissao: string, coluna: number) => void;
}

/** Largura de cada coluna de loja — a mesma no cabeçalho e nas linhas. */
const LARGURA_COLUNA = '5.5rem';

/** A marca de exceção da célula, num slot fixo para a coluna não dançar. */
function Marca({ estado }: { estado: EstadoAcao }) {
  const m = estado.alterada
    ? { icone: 'bi-pencil-fill', classe: 'text-warning', titulo: 'Alterado e ainda não salvo' }
    : estado.excecao;
  return (
    <span className="d-inline-block text-center" style={{ width: '1.1rem' }}>
      {m && (
        <i className={`bi ${m.icone} ${m.classe}`} style={{ fontSize: '0.8em' }} title={m.titulo}></i>
      )}
    </span>
  );
}

export function CustomAbasPermissoes({
  catalogo,
  estadoDe,
  aoAlternar,
  aoAlternarModulo,
  abasExtras = [],
  colunas,
  estadoNaColuna,
  aoAlternarNaColuna,
}: Props) {
  const emColunas = !!(colunas?.length && estadoNaColuna && aoAlternarNaColuna);
  const [aba, setAba] = useState<string>(catalogo[0]?.codigo ?? abasExtras[0]?.codigo ?? '');

  const permissoesDe = (m: Modulo) =>
    m.recursos.flatMap((r) => r.acoes.map((a) => `${m.codigo}.${r.codigo}.${a.codigo}`));

  const modulo = catalogo.find((m) => m.codigo === aba);
  const extra = abasExtras.find((e) => e.codigo === aba);

  return (
    <>
      <ul className="nav nav-tabs mb-3">
        {catalogo.map((m) => {
          const todas = permissoesDe(m);
          const ligadas = todas.filter((p) => estadoDe(p).ligada);
          const mexidas = todas.filter((p) => estadoDe(p).alterada).length;
          return (
            <li className="nav-item" key={m.codigo}>
              {/* Ícone um pouco menor e nome um pouco maior: assim os dois têm
                  o mesmo peso na linha. A contagem vem entre parênteses, na
                  mesma fonte — como badge ela competia com o nome da aba e
                  poluía uma barra que só precisa dizer onde estou. */}
              <button
                type="button"
                className={`nav-link ${aba === m.codigo ? 'active' : ''}`}
                onClick={() => setAba(m.codigo)}
              >
                <i
                  className={`bi ${ICONE[m.codigo] ?? 'bi-dot'} me-1`}
                  style={{ fontSize: '0.9em' }}
                ></i>
                <span style={{ fontSize: '1.02em' }}>{m.nome}</span>
                <span className="ms-1 fw-semibold" style={{ fontSize: '0.88em' }}>
                  ({ligadas.length}/{todas.length})
                </span>
                {/* O sinal de "tem coisa mexida aqui dentro" — sem ele, a
                    alteração numa aba some quando se troca de aba. */}
                {mexidas > 0 && (
                  <i
                    className="bi bi-pencil-fill text-warning ms-1"
                    style={{ fontSize: '0.8em' }}
                    title={`${mexidas} alteração(ões) não salva(s) neste módulo`}
                  ></i>
                )}
              </button>
            </li>
          );
        })}
        {abasExtras.map((e) => (
          <li className="nav-item" key={e.codigo}>
            <button
              type="button"
              className={`nav-link ${aba === e.codigo ? 'active' : ''}`}
              onClick={() => setAba(e.codigo)}
            >
              <i className={`bi ${e.icone} me-1`}></i>
              {e.nome}
            </button>
          </li>
        ))}
      </ul>

      {extra?.conteudo}

      {modulo && (
        <>
          {aoAlternarModulo && (
            <div className="row gx-3 align-items-center mb-2">
              <div className="col text-secondary small">
                Marcar ou desmarcar {modulo.nome} inteiro
              </div>
              <div className="col-auto">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id={`mod-${modulo.codigo}`}
                    checked={permissoesDe(modulo)
                      .filter((p) => estadoDe(p).editavel)
                      .every((p) => estadoDe(p).ligada)}
                    onChange={(e) => aoAlternarModulo(modulo, e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor={`mod-${modulo.codigo}`}></label>
                </div>
              </div>
            </div>
          )}

          {emColunas && (
            <div className="d-flex align-items-end border-bottom pb-2 mb-1">
              <div className="flex-grow-1 small text-secondary">
                Marque a loja para a pessoa receber o perfil nela; depois ajuste
                ação por ação.
              </div>
              {colunas!.map((c) => (
                <div
                  key={c.id}
                  className={`text-center ${c.alterada ? 'bg-warning-subtle rounded' : ''}`}
                  style={{ width: LARGURA_COLUNA, flexShrink: 0 }}
                >
                  <div className="small fw-semibold text-body">{c.rotulo}</div>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`col-${c.id}`}
                    checked={c.ativa}
                    onChange={c.aoAlternar}
                    title={c.ativa ? `Tirar a pessoa de ${c.rotulo}` : `Pôr a pessoa em ${c.rotulo}`}
                  />
                </div>
              ))}
            </div>
          )}

          <div className="row gx-3">
            {modulo.recursos.map((r) => (
              <div
                className={emColunas ? 'col-12 mb-2' : 'col-12 col-xxl-6 mb-0 mb-xxl-3'}
                key={r.codigo}
              >
                <h6 className="small text-uppercase text-secondary mt-2 mb-1">{r.nome}</h6>
                <ul className="list-group list-group-flush bg-none mb-2">
                  {r.acoes.map((a) => {
                    const permissao = `${modulo.codigo}.${r.codigo}.${a.codigo}`;
                    const estado = estadoDe(permissao);
                    if (emColunas) {
                      return (
                        <li className="list-group-item text-secondary px-0" key={permissao}>
                          <div className="d-flex align-items-center">
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                              <span className="d-block fw-medium text-body">{a.nome}</span>
                              <span
                                className="d-block lh-sm opacity-75"
                                style={{ fontSize: '0.78rem' }}
                              >
                                {estado.aviso ?? a.descricao}
                              </span>
                            </div>
                            {colunas!.map((c) => {
                              const e = estadoNaColuna!(permissao, c.id);
                              return (
                                <div
                                  key={c.id}
                                  className={`d-flex align-items-center justify-content-center ${
                                    e.alterada ? 'bg-warning-subtle rounded' : ''
                                  }`}
                                  style={{ width: LARGURA_COLUNA, flexShrink: 0 }}
                                >
                                  <div className="form-check form-switch mb-0">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      role="switch"
                                      id={`sw-${c.id}-${permissao}`}
                                      checked={e.ligada}
                                      disabled={!e.editavel || !c.ativa}
                                      onChange={() => aoAlternarNaColuna!(permissao, c.id)}
                                      aria-label={`${a.nome} — ${c.rotulo}`}
                                    />
                                  </div>
                                  <Marca estado={e} />
                                </div>
                              );
                            })}
                          </div>
                        </li>
                      );
                    }
                    return (
                      <li
                        className={`list-group-item text-secondary ${
                          estado.alterada ? 'bg-warning-subtle rounded' : ''
                        }`}
                        key={permissao}
                      >
                        <div className="row gx-3">
                          <div className="col-auto">
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id={`sw-${permissao}`}
                                checked={estado.ligada}
                                disabled={!estado.editavel}
                                onChange={() => aoAlternar(permissao)}
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`sw-${permissao}`}
                              ></label>
                            </div>
                          </div>
                          <div className="col" style={{ minWidth: 0 }}>
                            <label
                              className="d-block fw-medium text-body mb-0"
                              htmlFor={`sw-${permissao}`}
                              style={{ cursor: estado.editavel ? 'pointer' : 'default' }}
                            >
                              {a.nome}
                            </label>
                            {/* Menor e mais clara que o nome: ela explica, não
                                compete. Com 64 delas na tela, o peso do texto é
                                o que decide se a página se lê ou se cansa. */}
                            <span
                              className="d-block lh-sm opacity-75"
                              style={{ fontSize: '0.78rem' }}
                            >
                              {estado.aviso ?? a.descricao}
                            </span>
                          </div>
                          {/* As marcas fecham a linha na margem direita, em
                              SLOTS DE LARGURA FIXA — inclusive quando vazios.
                              É o que mantém a coluna do perfil sempre na mesma
                              posição, linha após linha: sem isso ela dança para
                              o lado toda vez que a de exceção aparece, e a
                              leitura vertical, que é a graça da coluna, se
                              perde. Mesmo tamanho nos três, pelo mesmo motivo. */}
                          <div className="col-auto text-end ps-0 d-flex align-items-center">
                            <span
                              className="d-inline-block text-center"
                              style={{ width: '1.1rem' }}
                            >
                              {estado.alterada && (
                                <i
                                  className="bi bi-pencil-fill text-warning"
                                  style={{ fontSize: '0.75em' }}
                                  title="Alterado e ainda não salvo"
                                ></i>
                              )}
                            </span>
                            <span
                              className="d-inline-block text-center"
                              style={{ width: '1.4rem' }}
                            >
                              {estado.excecao && (
                                <i
                                  className={`bi ${estado.excecao.icone} ${estado.excecao.classe}`}
                                  style={{ fontSize: '0.95em' }}
                                  title={estado.excecao.titulo}
                                ></i>
                              )}
                            </span>
                            <span
                              className="d-inline-block text-center"
                              style={{ width: '1.4rem' }}
                            >
                              {estado.perfil && (
                                <i
                                  className={`bi ${estado.perfil.icone} ${estado.perfil.classe}`}
                                  style={{ fontSize: '0.95em' }}
                                  title={estado.perfil.titulo}
                                ></i>
                              )}
                            </span>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}
